import { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

/*
 * MIGRATION NOTE (admin_impersonation_log) — run separately:
 *
 * CREATE TABLE admin_impersonation_log (
 *   id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
 *   admin_user_id UUID NOT NULL,
 *   admin_email TEXT,
 *   target_user_id UUID NOT NULL,
 *   target_name TEXT,
 *   action TEXT NOT NULL CHECK (action IN ('start','stop')),
 *   ip_address TEXT,
 *   created_at TIMESTAMPTZ NOT NULL DEFAULT now()
 * );
 */

interface ImpersonateContextType {
  impersonatedUserId: string | null;
  impersonatedName: string | null;
  impersonatedCompany: string | null;
  startImpersonate: (userId: string, name: string, company?: string) => void;
  stopImpersonate: () => void;
  isImpersonating: boolean;
}

const ImpersonateContext = createContext<ImpersonateContextType>({
  impersonatedUserId: null,
  impersonatedName: null,
  impersonatedCompany: null,
  startImpersonate: () => {},
  stopImpersonate: () => {},
  isImpersonating: false,
});

export const useImpersonate = () => useContext(ImpersonateContext);

export function ImpersonateProvider({ children }: { children: ReactNode }) {
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [impersonatedName, setImpersonatedName] = useState<string | null>(null);
  const [impersonatedCompany, setImpersonatedCompany] = useState<string | null>(null);

  const startImpersonate = (userId: string, name: string, company?: string) => {
    setImpersonatedUserId(userId);
    setImpersonatedName(name);
    setImpersonatedCompany(company || null);

    // NF525 JET: log IMPERSONATION_START (fire-and-forget)
    supabase.auth
      .getUser()
      .then(({ data }) => {
        const adminId = data?.user?.id;
        const adminEmail = data?.user?.email;
        if (adminId) {
          supabase
            .from('fiscal_events')
            .insert({
              event_type: 'IMPERSONATION_START',
              user_id: adminId,
              details: { admin_user_id: adminId, target_user_id: userId, target_name: name },
            } as any)
            // .then(success, failure) — .catch() n'est pas sur PromiseLike
            .then(
              () => {},
              (e) => console.error('[NF525] Failed to log IMPERSONATION_START:', e),
            );

          // Also log to dedicated audit table (if it exists)
          supabase
            .from('admin_impersonation_log' as any)
            .insert({
              admin_user_id: adminId,
              admin_email: adminEmail,
              target_user_id: userId,
              target_name: name,
              action: 'start',
            } as any)
            .then(
              () => {},
              () => { /* table may not exist yet */ },
            );
        }
      })
      .catch(() => {});
  };

  const stopImpersonate = () => {
    const previousTargetId = impersonatedUserId;
    const previousTargetName = impersonatedName;

    setImpersonatedUserId(null);
    setImpersonatedName(null);
    setImpersonatedCompany(null);

    // NF525 JET: log IMPERSONATION_STOP (fire-and-forget)
    supabase.auth
      .getUser()
      .then(({ data }) => {
        const adminId = data?.user?.id;
        const adminEmail = data?.user?.email;
        if (adminId) {
          supabase
            .from('fiscal_events')
            .insert({
              event_type: 'IMPERSONATION_STOP',
              user_id: adminId,
              details: {
                admin_user_id: adminId,
                target_user_id: previousTargetId,
                target_name: previousTargetName,
              },
            } as any)
            .then(
              () => {},
              (e) => console.error('[NF525] Failed to log IMPERSONATION_STOP:', e),
            );

          // Also log to dedicated audit table (if it exists)
          supabase
            .from('admin_impersonation_log' as any)
            .insert({
              admin_user_id: adminId,
              admin_email: adminEmail,
              target_user_id: previousTargetId,
              target_name: previousTargetName,
              action: 'stop',
            } as any)
            .then(
              () => {},
              () => { /* table may not exist yet */ },
            );
        }
      })
      .catch(() => {});
  };

  return (
    <ImpersonateContext.Provider
      value={{
        impersonatedUserId,
        impersonatedName,
        impersonatedCompany,
        startImpersonate,
        stopImpersonate,
        isImpersonating: !!impersonatedUserId,
      }}
    >
      {children}
    </ImpersonateContext.Provider>
  );
}
